import { useEffect, useMemo, useState } from "react";
import { useContextoApp } from "@/app/contexto";
import { useApiResource } from "@/hooks/useApiResource";
import { useAsyncAction } from "@/hooks/useAsyncAction";
import { api, type Adjunto, type Avance, type AvanceAdmin, type ItemRecorrido, type ListadoNovedades, type Novedad } from "@/lib/api";
import { rolesDe } from "@/lib/navegacion";
import { avisaDeEntrada, esAvanceAdmin, filtroNovedadesPaso, nodosRecorrido, pasoActual, resumenRecorrido } from "@/lib/avance";

export const POR_PAGINA = 10;

export type DialogoAvance =
  | { tipo: "novedad"; novedad: Novedad | null } // novedad null = nueva
  | { tipo: "borrar"; novedad: Novedad }
  | { tipo: "inicio" }
  | { tipo: "cumplir"; hitoId: number; nombre: string; avisar: boolean } // un sub-hito de Configuración → Avance de obra
  | { tipo: "desmarcar"; hitoId: number; nombre: string }
  | null;

/** Novedades que se muestran en el detalle de un paso del recorrido. */
export const NOVEDADES_PASO = 3;

/**
 * Lógica de Avance (sin JSX). El backend arma el % completado y el recorrido de la obra (lectores) o, además, el
 * cronograma detallado (admin); acá se piden el avance y las novedades (de a POR_PAGINA, con "Ver más"), se guarda el
 * paso elegido del recorrido (de entrada, el actual) y se piden las últimas novedades de ese paso. Leen los cuatro roles.
 */
export function useAvanceTab() {
  const { fid, membresia } = useContextoApp();
  const base = `/api/fideicomisos/${fid}/avance`;
  const esAdmin = membresia.rol === "admin";

  const avance = useApiResource<Avance>(base);
  const primera = useApiResource<ListadoNovedades>(`${base}/novedades?limite=${POR_PAGINA}`);

  // Páginas siguientes: se acumulan acá y se descartan cuando se vuelve a pedir la primera.
  const [siguientes, setSiguientes] = useState<Novedad[]>([]);
  const [hayMasSiguientes, setHayMasSiguientes] = useState<boolean | null>(null);
  useEffect(() => {
    setSiguientes([]);
    setHayMasSiguientes(null);
  }, [primera.data]);

  const accionMas = useAsyncAction((antesDe: number) => api.get<ListadoNovedades>(`${base}/novedades?limite=${POR_PAGINA}&antes_de=${antesDe}`));
  const accionBorrar = useAsyncAction((novedadId: number) => api.delete<void>(`${base}/novedades/${novedadId}`).then(() => true));

  const accionDesmarcar = useAsyncAction((hitoId: number) => api.delete<void>(`${base}/hitos/${hitoId}/completar`).then(() => true));
  const accionNoCorresponde = useAsyncAction((hitoId: number, valor: boolean) =>
    api.patch<unknown>(`${base}/hitos/${hitoId}`, { no_corresponde: valor }).then(() => true),
  );
  const accionSubirGantt = useAsyncAction((archivo: File) => {
    const f = new FormData();
    f.append("archivo", archivo);
    return api.upload<Adjunto>(`${base}/cronograma/adjuntos`, f);
  });
  const accionBorrarGantt = useAsyncAction((adjuntoId: number) => api.delete<void>(`/api/fideicomisos/${fid}/adjuntos/${adjuntoId}`).then(() => true));

  const novedades = useMemo(() => [...(primera.data?.items ?? []), ...siguientes], [primera.data, siguientes]);
  const hayMas = hayMasSiguientes ?? primera.data?.hay_mas ?? false;

  const verMas = async () => {
    const ultima = novedades[novedades.length - 1];
    if (!ultima) return;
    const pagina = await accionMas.run(ultima.id);
    if (!pagina) return;
    setSiguientes((s) => [...s, ...pagina.items]);
    setHayMasSiguientes(pagina.hay_mas);
  };

  const [dialogo, setDialogo] = useState<DialogoAvance>(null);

  const datos = avance.data ?? null;
  const admin: AvanceAdmin | null = datos && esAvanceAdmin(datos) ? datos : null;
  const pasos = useMemo(() => datos?.recorrido ?? [], [datos]);
  const actual = useMemo(() => pasoActual(pasos), [pasos]);
  const [elegido, setElegido] = useState<string | null>(null);
  const seleccionado = pasos.some((p) => p.clave === elegido) ? elegido : actual;
  const indice = pasos.findIndex((p) => p.clave === seleccionado);
  const paso = indice >= 0 ? pasos[indice]! : null;
  const filtroPaso = paso ? filtroNovedadesPaso(paso) : null;
  const novedadesPaso = useApiResource<ListadoNovedades>(filtroPaso ? `${base}/novedades?limite=${NOVEDADES_PASO}&${filtroPaso}` : null);
  const recargarTodo = () => {
    avance.recargar();
    primera.recargar();
    novedadesPaso.recargar();
  };

  return {
    fid,
    esAdmin,
    /** Terreno no está en el menú: se llega desde acá y desde el Inicio (Mati, 2026-11-03). */
    verTerreno: rolesDe("/terreno").includes(membresia.rol),
    avance: datos,
    admin,
    completado: datos?.completado ?? null,
    resumen: resumenRecorrido(pasos),
    nodos: nodosRecorrido(pasos),
    actual,
    seleccionado,
    paso,
    numeroPaso: indice + 1,
    totalPasos: pasos.length,
    elegirPaso: setElegido,
    anterior: indice > 0 ? () => setElegido(pasos[indice - 1]!.clave) : null,
    siguiente: indice >= 0 && indice < pasos.length - 1 ? () => setElegido(pasos[indice + 1]!.clave) : null,
    /** null = el paso no tiene novedades propias (la firma, un hito, el final). */
    novedadesPaso: filtroPaso ? (novedadesPaso.data?.items ?? []) : null,
    cargandoNovedadesPaso: novedadesPaso.loading && !novedadesPaso.data,
    errorNovedadesPaso: novedadesPaso.data ? null : (novedadesPaso.error?.message ?? null),
    /** Hay tareas en el cronograma (o hitos de Configuración): se puede publicar el avance. */
    hayTareas: (datos?.completado.total ?? 0) > 0,
    porHitos: datos?.fuente === "hitos",
    novedades,
    hayMas,
    cargando: (avance.loading && !avance.data) || (primera.loading && !primera.data),
    error: avance.data ? null : (avance.error?.message ?? null),
    errorNovedades: primera.data ? null : (primera.error?.message ?? null),
    verMas,
    cargandoMas: accionMas.loading,
    errorMas: accionMas.error,
    recargar: recargarTodo,
    dialogo,
    abrirNovedad: (novedad: Novedad | null) => setDialogo({ tipo: "novedad", novedad }),
    abrirBorrar: (novedad: Novedad) => {
      accionBorrar.limpiarError();
      setDialogo({ tipo: "borrar", novedad });
    },
    confirmarBorrar: async () => {
      if (dialogo?.tipo !== "borrar") return;
      if (await accionBorrar.run(dialogo.novedad.id)) {
        setDialogo(null);
        recargarTodo();
      }
    },
    borrando: accionBorrar.loading,
    errorBorrar: accionBorrar.error,
    cerrar: () => setDialogo(null),
    /** Cierra cualquier diálogo (novedad o del control interno) y vuelve a pedir avance y novedades: pudo haber
     *  creado o editado la novedad, subido o borrado fotos o cambiado el inicio. */
    cerrarYRecargar: () => {
      setDialogo(null);
      recargarTodo();
    },
    /** Sub-hitos (recorrido por hitos): marcar cumplido abre el formulario; desmarcar pide confirmación. */
    abrirCumplir: (item: ItemRecorrido) =>
      item.hito_id != null && setDialogo({ tipo: "cumplir", hitoId: item.hito_id, nombre: item.nombre, avisar: avisaDeEntrada(item) }),
    /** "No corresponde" (demolición, DGIUR, plusvalía…): cuenta como hecho, sin novedad ni aviso. */
    noCorresponde: async (item: ItemRecorrido, valor: boolean) => {
      if (item.hito_id == null) return;
      if (await accionNoCorresponde.run(item.hito_id, valor)) recargarTodo();
    },
    errorNoCorresponde: accionNoCorresponde.error,
    abrirDesmarcar: (item: ItemRecorrido) => {
      if (item.hito_id == null) return;
      accionDesmarcar.limpiarError();
      setDialogo({ tipo: "desmarcar", hitoId: item.hito_id, nombre: item.nombre });
    },
    confirmarDesmarcar: async () => {
      if (dialogo?.tipo !== "desmarcar") return;
      if (await accionDesmarcar.run(dialogo.hitoId)) {
        setDialogo(null);
        recargarTodo();
      }
    },
    desmarcando: accionDesmarcar.loading,
    errorDesmarcar: accionDesmarcar.error,
    abrirInicio: () => setDialogo({ tipo: "inicio" }),
    rutaDescargaGantt: (a: Adjunto) => `/api/fideicomisos/${fid}/adjuntos/${a.id}/descarga`,
    subirGantt: async (archivo: File) => {
      accionBorrarGantt.limpiarError();
      if (await accionSubirGantt.run(archivo)) avance.recargar();
    },
    borrarGantt: async (a: Adjunto) => {
      accionSubirGantt.limpiarError();
      if (await accionBorrarGantt.run(a.id)) avance.recargar();
    },
    subiendoGantt: accionSubirGantt.loading,
    errorGantt: accionSubirGantt.error ?? accionBorrarGantt.error,
  };
}
